import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Card } from '@codes/ui-kit/components/ui/card';
import { Image } from '@codes/ui-kit/components/ui/image';
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from '@codes/ui-kit/components/ui/pagination';
import { CatalogView } from '@lemoncloud/codes-goods-api';
import { usePagination, useSelectedStore, useStereoIcon } from '@web/app/shared';
import { ChevronRight } from 'lucide-react';

import { useFetchCatalogs } from '@codes/catalog';
import { Avatar, SharedSpinner } from '@codes/ui-kit';

const stereoFilterOptions = ['all', 'solution', 'front', 'back', 'cdk', 'app'];

interface StereoAggrItem {
    key: string;
    val: number;
}

export const CatalogListPage = () => {
    const { t } = useTranslation();
    const { i18n } = useTranslation();
    const getStereoIcon = useStereoIcon();
    const [searchParams, setSearchParams] = useSearchParams();

    const page = parseInt(searchParams.get('page') || '0', 10);
    const stereo = searchParams.get('stereo') || 'all';
    const name = searchParams.get('name') || '';
    const limit = 12;

    const [aggrData, setAggrData] = useState<Record<string, number>>({});

    const navigate = useNavigate();
    const { workspace, project } = useSelectedStore();

    const queryParams = {
        page,
        limit,
        ...(stereo !== 'all' && { stereo }),
        ...(name && { name }),
        view: 'user',
    };

    const { data: catalogsData, isLoading } = useFetchCatalogs(queryParams);

    useEffect(() => {
        if (catalogsData?.aggr) {
            const newAggrData = catalogsData.aggr.reduce((acc: Record<string, number>, item: StereoAggrItem) => {
                acc[item.key] = item.val;
                return acc;
            }, {});

            const keysToSum = ['back', 'cdk', 'front', 'app', 'solution'];
            const totalSum = keysToSum.reduce((sum, key) => {
                return sum + (newAggrData[key] || 0);
            }, 0);

            setAggrData(prevAggrData => {
                const updatedAggrData: Record<string, number> = { ...newAggrData, all: totalSum };
                const hasChanges = Object.keys(updatedAggrData).some(key => updatedAggrData[key] !== prevAggrData[key]);
                return hasChanges ? updatedAggrData : prevAggrData;
            });
        }
    }, [catalogsData?.aggr]);

    const { paginationRange } = usePagination({
        totalCount: catalogsData?.total || 0,
        pageSize: limit,
        currentPage: page,
        siblingCount: 1,
    });

    const handlePageChange = (newPage: number) => {
        setSearchParams(prev => {
            prev.set('page', newPage.toString());
            return prev;
        });
    };

    const handleStereoChange = (newStereo: string) => {
        setSearchParams(prev => {
            prev.set('stereo', newStereo);
            prev.set('page', '0');
            return prev;
        });
    };

    const handleCatalogClick = (catalogId: string) => {
        if (workspace && project) {
            navigate(`/w/${workspace.id}/p/${project.id}/catalogs/${catalogId}`);
        } else if (workspace) {
            navigate(`/w/${workspace.id}/catalogs/${catalogId}`);
        }
    };

    const getIconImage = (catalog: CatalogView | undefined) => {
        if (!catalog) {
            return '/assets/icons/empty.svg';
        }

        const thumb = catalog.thumbs && catalog.thumbs.length >= 1 ? catalog.thumbs[0] : null;

        if (thumb) {
            return thumb;
        }
        return getStereoIcon(catalog.stereo as string);
    };

    return (
        <div className="flex-1 py-[34px] px-[68px] max-md:p-5 w-full h-full flex justify-center">
            <div className="w-full flex-1 flex flex-col">
                <div className="text-[20px] font-medium mb-4">Service Catalog</div>
                <div className="mb-[35px] -mx-5">
                    <div className="flex items-center gap-3 overflow-auto py-1 scrollbar-hide px-5">
                        {stereoFilterOptions.map(option => (
                            <div
                                key={option}
                                className="flex items-center justify-center rounded-xl cursor-pointer"
                                style={{
                                    background:
                                        stereo === option
                                            ? 'linear-gradient(102deg, hsl(var(--gradient1)) -3.17%, hsl(var(--gradient2)) 40.51%, hsl(var(--gradient3)))'
                                            : 'var(--gray-200)',
                                    padding: '0.6px',
                                    boxShadow:
                                        stereo === option
                                            ? '0 0 8px 0 rgba(255, 115, 0, 0.28)'
                                            : '0 0 4px 0 rgba(0, 0, 0, 0.08)',
                                }}
                                onClick={() => handleStereoChange(option)}
                            >
                                <div
                                    className={`w-full flex items-center justify-center rounded-xl font-medium gap-1 h-10 px-3 ${
                                        stereo === option
                                            ? 'bg-[#FFF6EE] dark:bg-[#3B2C24] text-point'
                                            : 'bg-background text-gray-700'
                                    }`}
                                >
                                    <span>{option === 'all' ? 'ALL' : option.toUpperCase()}</span>
                                    {!!aggrData[option] && <span>{aggrData[option] || ''}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                {/*  */}
                {isLoading ? (
                    <div className="w-full flex justify-center items-center">
                        <SharedSpinner />
                    </div>
                ) : catalogsData?.data.length === 0 ? (
                    <div className="text-lg text-gray-500 text-center pt-[20%]">{t('project.empty')}</div>
                ) : (
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(350px,1fr))] max-xs:grid-cols-1 gap-[26px] max-md:gap-[18px]">
                        {catalogsData?.data.map(catalog => (
                            <Card
                                key={catalog.id}
                                className={`relative h-[294px] cursor-pointer flex flex-col p-0 overflow-hidden ${
                                    catalog.stereo === 'front'
                                        ? 'bg-[#FAE8D8] dark:bg-[#EDCDB1]'
                                        : catalog.stereo === 'back'
                                        ? 'bg-[#F5FBDE] dark:bg-[#D5DFA9]'
                                        : catalog.stereo === 'app'
                                        ? 'bg-[#FEF9D6] dark:bg-[#F9E6B4]'
                                        : catalog.stereo === 'solution'
                                        ? 'bg-[#F0EFFF] dark:bg-[#ABA9CF]'
                                        : catalog.stereo === 'cdk'
                                        ? 'bg-[#ECF3FF] dark:bg-[#C1D6F9]'
                                        : 'bg-[#F5F5F5] dark:bg-[#2B2B2B]'
                                }`}
                                onClick={() => handleCatalogClick(catalog.id as string)}
                            >
                                {/* TODO: 뱃지 추가 */}
                                {/* <div className="absolute top-0 left-0 flex">
                                    <div className="px-[18px] h-6 bg-[#FFA600] text-white text-sm font-medium flex items-center justify-center rounded-tl-[10px]">
                                        NEW
                                    </div>
                                    <div className="px-[18px] h-6 bg-[#FF3464] text-white text-sm font-medium flex items-center justify-center rounded-br-[10px]">
                                        HOT
                                    </div>
                                </div> */}
                                <div className="absolute top-[30px] right-7">
                                    <Avatar className="w-[72px] h-[72px] rounded-[18px] border-none shadow-[0px_0px_16px_0px_rgba(0,0,0,0.12)]">
                                        <Image
                                            src={getIconImage(catalog)}
                                            darkSrc={getIconImage(catalog)}
                                            alt={catalog.stereo}
                                        />
                                    </Avatar>
                                </div>
                                <div className="mt-auto bg-card h-[202px] pt-[49px] pb-[22px] px-[18px] flex flex-col">
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <div className="text-[20px] max-xs:text-[18px] font-semibold text-ellipsis break-words line-clamp-1 overflow-hidden capitalize">
                                                {(i18n.language === 'en' && catalog.nameEn) || catalog.name}
                                            </div>
                                            {/* TODO: 상태 텍스트 */}
                                            {/* <div className="text-[#2189FF] font-semibold">이용중</div> */}
                                        </div>
                                        <p className="mt-1 text-gray-900 font-medium max-xs:text-sm text-ellipsis break-words line-clamp-2 overflow-hidden">
                                            {(i18n.language === 'en' && catalog.summaryEn) || catalog.summary}
                                        </p>
                                    </div>
                                    <div className="flex items-center justify-between mt-auto">
                                        {catalog.stereo && (
                                            <div className="text-point font-medium uppercase" key={catalog.stereo}>
                                                {catalog.stereo}
                                            </div>
                                        )}
                                        <button className="flex items-center justify-center">
                                            <div className="text-gray-900 hover:underline">
                                                {t('project.serviceDetails')}
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-gray-900" />
                                        </button>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
                {!isLoading && catalogsData?.total !== 0 && (
                    <Pagination className="mt-auto">
                        <PaginationContent>
                            <PaginationItem>
                                <PaginationPrevious
                                    onClick={() => handlePageChange(page - 1)}
                                    aria-disabled={page <= 0}
                                    tabIndex={page <= 0 ? -1 : undefined}
                                    className={page <= 0 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                />
                            </PaginationItem>
                            {paginationRange?.map((pageNumber, index) => (
                                <PaginationItem key={index}>
                                    {pageNumber === '...' ? (
                                        <PaginationEllipsis />
                                    ) : (
                                        <PaginationLink
                                            className="cursor-pointer"
                                            onClick={() => handlePageChange(Number(pageNumber))}
                                            isActive={page === Number(pageNumber)}
                                        >
                                            {Number(pageNumber) + 1}
                                        </PaginationLink>
                                    )}
                                </PaginationItem>
                            ))}
                            <PaginationItem>
                                <PaginationNext
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={page === Math.ceil((catalogsData?.total || 0) / limit) - 1}
                                    aria-disabled={page === Math.ceil((catalogsData?.total || 0) / limit) - 1}
                                    className={
                                        page === Math.ceil((catalogsData?.total || 0) / limit) - 1
                                            ? 'pointer-events-none opacity-50'
                                            : 'cursor-pointer'
                                    }
                                />
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                )}
            </div>
        </div>
    );
};
